import { type ArtDirection } from '@/scripts/core/art-direction'
import { UcoastVideo } from '@/scripts/core/ucoast-video'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { type Hls } from '@/scripts/global'

type IntentPreparation = {
	owner: HTMLElement
	prepare: (signal: AbortSignal) => Promise<void>
	allowed: () => boolean
	controller: AbortController
	selected: boolean
}

export class MediaManager {
	private pendingIntent?: IntentPreparation
	private activeIntent?: IntentPreparation

	queueIntentPreparation(
		owner: HTMLElement,
		prepare: IntentPreparation['prepare'],
		allowed: () => boolean
	) {
		this.pendingIntent?.controller.abort()
		if (!this.activeIntent?.selected) this.activeIntent?.controller.abort()
		const work = {
			owner,
			prepare,
			allowed,
			controller: new AbortController(),
			selected: false,
		}
		this.pendingIntent = work
		void this.prepareNext()
		return () => {
			if (!work.selected) work.controller.abort()
		}
	}

	promoteIntentPreparation(owner: HTMLElement) {
		for (const work of [this.pendingIntent, this.activeIntent])
			if (work?.owner === owner) work.selected = true
	}

	cancelIntentPreparation(owner: HTMLElement) {
		for (const work of [this.pendingIntent, this.activeIntent])
			if (work?.owner === owner) work.controller.abort()
	}

	private visibleMediaIsReady() {
		return this.videos.every((video) => {
			if (!video.isTouchingViewport() || video.isHiddenForViewport())
				return true
			if (
				!video.videoEl.paused &&
				video.videoEl.readyState < HTMLMediaElement.HAVE_FUTURE_DATA
			)
				return false
			return (
				!video.posterFirst ||
				['playing', 'paused', 'failed', 'autoplay-blocked'].includes(
					video.dataset.mediaState ?? ''
				)
			)
		})
	}

	private async prepareIntent() {
		const work = this.pendingIntent
		if (!work || document.hidden || !this.visibleMediaIsReady()) return
		this.pendingIntent = undefined
		if (
			work.controller.signal.aborted ||
			!work.owner.isConnected ||
			!work.allowed()
		)
			return
		this.activeIntent = work
		let timer: number | undefined
		try {
			await Promise.race([
				work.prepare(work.controller.signal),
				new Promise<void>((resolve) => {
					work.controller.signal.addEventListener(
						'abort',
						() => resolve(),
						{ once: true }
					)
					timer = window.setTimeout(
						() => work.controller.abort(),
						8000
					)
				}),
			])
		} catch {
			// A failed speculative request must never alter selected media.
		} finally {
			window.clearTimeout(timer)
			this.activeIntent = undefined
		}
	}

	hlsRequired: boolean
	hlsLibraryLoaded: boolean
	initialLoadStarted: boolean
	Hls?: Hls
	playCallbacks: (() => void)[]
	hlsLibIsSupported = false
	videos: UcoastVideo[]
	videoObserver: IntersectionObserver
	private preparationQueue = new Set<UcoastVideo | ArtDirection>()
	private preparing = false
	private nearObserver = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				const video = entry.target as UcoastVideo | ArtDirection
				if (!video.posterFirst) continue
				if (entry.isIntersecting) this.preparationQueue.add(video)
				else {
					this.preparationQueue.delete(video)
					if (isVideoComponent(video)) video.pause()
				}
			}
			void this.prepareNext()
		},
		{ rootMargin: '100% 0px' }
	)

	private async prepareNext() {
		if (this.preparing) {
			if (this.preparationQueue.size && !this.activeIntent?.selected)
				this.activeIntent?.controller.abort()
			return
		}
		if (document.hidden) return
		this.preparing = true
		try {
			while (this.preparationQueue.size && !document.hidden) {
				const candidates = [...this.preparationQueue].filter(
					(video) => video.isConnected && !video.isHiddenForViewport()
				)
				candidates.sort(
					(a, b) =>
						Number(b.isTouchingViewport()) -
							Number(a.isTouchingViewport()) ||
						a.getBoundingClientRect().top -
							b.getBoundingClientRect().top
				)
				const video = candidates[0]
				if (!video) break
				this.preparationQueue.delete(video)
				await video.prepare()
				if (isVideoComponent(video)) {
					if (!video.userPlayRequested)
						await waitForHeroPosterQuiet(video)
					void video.play()
					if (video.isTouchingViewport())
						await video.waitUntilSettled()
				}
			}
			await this.prepareIntent()
		} finally {
			this.preparing = false
			if (
				(this.pendingIntent && this.visibleMediaIsReady()) ||
				[...this.preparationQueue].some(
					(media) => media.isConnected && !media.isHiddenForViewport()
				)
			)
				void this.prepareNext()
		}
	}

	private hlsLoadPromise?: Promise<void>

	constructor() {
		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				this.activeIntent?.controller.abort()
				this.pendingIntent?.controller.abort()
			}
			for (const video of this.videos) {
				if (document.hidden) video.pause()
				else if (video.posterFirst) this.preparationQueue.add(video)
				else void video.play()
			}
			void this.prepareNext()
		})
		window.addEventListener('resize', () => {
			for (const video of this.videos) {
				if (video.isHiddenForViewport()) video.pause()
				else if (video.posterFirst && video.isTouchingViewport())
					this.preparationQueue.add(video)
			}
			void this.prepareNext()
		})
		this.hlsLibraryLoaded = false
		this.initialLoadStarted = false
		this.playCallbacks = []
		this.hlsRequired = this.isHlsRequired()
		this.videos = []
		this.videoObserver = new IntersectionObserver(
			this.onIntersection.bind(this),
			{
				rootMargin: '0px 0px -50px 0px',
			}
		)
	}

	addDeferredMedia(media: ArtDirection) {
		this.nearObserver.observe(media)
	}
	removeDeferredMedia(media: ArtDirection) {
		this.nearObserver.unobserve(media)
		this.preparationQueue.delete(media)
	}

	// public methods
	prepareImageReveals(container: ParentNode) {
		container.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
			img.loading = 'eager'
			const fadeTarget = img.parentElement instanceof HTMLPictureElement
				? img.parentElement
				: img
			handleImageFadeIn(img, fadeTarget, false, true)
		})
	}

	async initialLoad() {
		if (this.initialLoadStarted) return
		this.initialLoadStarted = true

		const runInitialLoad = () => {
			const imageFadeTargets: {
				img: HTMLImageElement
				fadeTarget: HTMLElement
			}[] = []
			document
				.querySelectorAll('picture, img.no-picture')
				.forEach((element: Element) => {
					if (element instanceof HTMLPictureElement) {
						const img = q.os<HTMLImageElement>('img', element)
						if (img) {
							imageFadeTargets.push({
								img,
								fadeTarget: element,
							})
						}
					} else if (
						element instanceof HTMLImageElement &&
						element.classList.contains('no-picture')
					) {
						imageFadeTargets.push({
							img: element,
							fadeTarget: element,
						})
					}
				})

			const skipFadeIns = imageFadeTargets.map(({ img, fadeTarget }) =>
				shouldSkipFadeIn(img, fadeTarget)
			)
			imageFadeTargets.forEach(({ img, fadeTarget }, index) => {
				handleImageFadeIn(img, fadeTarget, skipFadeIns[index])
			})

			document
				.querySelectorAll<ArtDirection>(
					'art-direction[data-media-deferred]'
				)
				.forEach((media) => this.addDeferredMedia(media))
			this.loadEarlyVideos()
		}

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', runInitialLoad, {
				once: true,
			})
			return
		}

		runInitialLoad()
	}

	loadEarlyVideos() {
		const videos = q.ol<UcoastVideo>('ucoast-video[data-retry="true"]')
		if (!videos) return
		videos.forEach((video) => {
			if (this.videos.some((item) => video === item)) return
			video.unMarkForRetry()
			void video.onConnectedCallback()
		})
	}

	onIntersection(elements: IntersectionObserverEntry[]) {
		elements.forEach((element) => {
			if (!isVideoComponent(element.target)) return
			if (element.isIntersecting) {
				const elementTarget: UcoastVideo = element.target
				if (elementTarget.posterFirst) {
					this.preparationQueue.add(elementTarget)
					void this.prepareNext()
				} else void elementTarget.play()
			} else {
				const elementTarget: UcoastVideo = element.target
				elementTarget.pause()
			}
		})
	}

	private onMediaReady = () => {
		void this.prepareNext()
	}
	private onMediaWaiting = () => {
		if (!this.activeIntent?.selected) this.activeIntent?.controller.abort()
	}
	addVideo(video: UcoastVideo) {
		if (!this.videos.some((item) => video === item)) {
			video.videoEl.addEventListener('waiting', this.onMediaWaiting)
			video.videoEl.addEventListener('canplay', this.onMediaReady)
			video.videoEl.addEventListener('playing', this.onMediaReady)
			this.videoObserver.observe(video)
			if (video.posterFirst) this.nearObserver.observe(video)
			this.videos.push(video)
		}
	}
	removeVideo(video: UcoastVideo) {
		video.videoEl.removeEventListener('waiting', this.onMediaWaiting)
		video.videoEl.removeEventListener('canplay', this.onMediaReady)
		video.videoEl.removeEventListener('playing', this.onMediaReady)
		this.videoObserver.unobserve(video)
		this.nearObserver.unobserve(video)
		this.preparationQueue.delete(video)
		this.videos = this.videos.filter((item) => video !== item)
	}

	// hls

	private isHlsRequired() {
		const video = document.createElement('video')
		return video.canPlayType('application/vnd.apple.mpegurl') === ''
	}

	async ensureHlsFor(video?: UcoastVideo) {
		if (!this.hlsRequired) return
		if (this.hlsLibraryLoaded) {
			if (video) await video.onLibraryLoad()
			return
		}
		this.hlsLoadPromise ??= this.loadHls()
		await this.hlsLoadPromise
		if (!this.hlsLibraryLoaded) {
			video?.useMp4Fallback()
			return
		}
		if (video) await video.onLibraryLoad()
	}

	private async loadHls() {
		try {
			// ignoring because types are defined through global
			// @ts-ignore
			const hlsLib: Hls = await import('hls.js/dist/hls.light.min.js')
			this.hlsLibIsSupported = hlsLib.isSupported()
			this.Hls = hlsLib
			this.hlsLibraryLoaded = true
		} catch (error) {
			this.hlsLoadPromise = undefined
			console.error('Failed to load the HLS library', error)
		}
	}

	async playAllInContainer(container: HTMLElement) {
		const videosInContainer = q.ol<UcoastVideo>('ucoast-video', container)
		if (!videosInContainer) return
		videosInContainer.forEach((video) => {
			void video.playEventOn()
		})
	}

	async pauseAllInContainer(container: HTMLElement) {
		const videosInContainer = q.ol<UcoastVideo>('ucoast-video', container)
		if (!videosInContainer) return
		videosInContainer.forEach((video) => {
			void video.playEventOff()
		})
	}
}

function waitForHeroPosterQuiet(video: UcoastVideo): Promise<void> {
	const hero = video.closest('[data-product-hero-media]')
	if (!hero) return Promise.resolve()
	const image = hero.querySelector('img')
	return new Promise((resolve) => {
		let settled = false
		const finish = () => {
			if (settled) return
			settled = true
			requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
		}
		const idle = (
			window as Window & {
				requestIdleCallback?: (
					callback: () => void,
					options?: { timeout: number }
				) => number
			}
		).requestIdleCallback
		const afterPaint = () => {
			if (typeof idle === 'function')
				idle(() => finish(), { timeout: 400 })
			else window.setTimeout(finish, 400)
		}
		if (!image || image.complete) {
			afterPaint()
			return
		}
		image.addEventListener('load', afterPaint, { once: true })
		image.addEventListener('error', afterPaint, { once: true })
		window.setTimeout(afterPaint, 2000)
	})
}

export function isVideoComponent(
	obj: HTMLElement | Element
): obj is UcoastVideo {
	if (!obj) return false
	if (obj.localName !== 'ucoast-video') return false
	return true
}

function handleImageFadeIn(
	img: HTMLImageElement,
	fadeTarget: HTMLElement,
	skipFadeIn: boolean,
	revealOnTimeout = false
): void {
	const fadeDuration = 300

	if (skipFadeIn) {
		fadeTarget.style.opacity = '1'
		fadeTarget.style.removeProperty('transition')
		return
	}

	fadeTarget.style.opacity = '0'
	fadeTarget.style.transition = `opacity ${fadeDuration}ms ease-in-out`

	let revealed = false
	let fallback: number | undefined
	const reveal = () => {
		if (revealed) return
		revealed = true
		window.clearTimeout(fallback)
		img.removeEventListener('load', decodeAndReveal)
		img.removeEventListener('error', reveal)
		requestAnimationFrame(() => {
			fadeTarget.style.opacity = '1'
		})
	}
	const decodeAndReveal = () => {
		img.decode().then(reveal).catch(reveal)
	}
	if (revealOnTimeout) fallback = window.setTimeout(reveal, 2000)

	img.addEventListener('load', decodeAndReveal, { once: true })
	img.addEventListener('error', reveal, { once: true })
	if (!img.complete) return
	if (img.naturalWidth > 0) decodeAndReveal()
	else reveal()
}

function shouldSkipFadeIn(
	img: HTMLImageElement,
	fadeTarget: HTMLElement
): boolean {
	if (img.complete && img.naturalWidth > 0) return true
	if (img.loading === 'eager') return true
	if (img.getAttribute('fetchpriority') === 'high') return true
	if (img.closest('.header__heading-logo-media')) return true

	const rect = fadeTarget.getBoundingClientRect()
	return (
		rect.width > 0 &&
		rect.height > 0 &&
		rect.bottom > 0 &&
		rect.right > 0 &&
		rect.top < window.innerHeight &&
		rect.left < window.innerWidth
	)
}
